import { api, hasApiBaseUrl, withFallback } from "./api/client";
import { documentos as mock } from "@/lib/mock-data";
import { DEFAULT_DOCUMENTOS_CONFIG, SUBCARPETA_LABELS } from "@/lib/documentos";
import type {
  Documento,
  DocumentosConfig,
  DocumentosTree,
  DocumentoUploadTicket,
} from "@/types";

export interface DocumentosListParams {
  ambito?: string;
  licitacionId?: string;
  subcarpeta?: string;
  tag?: string;
  q?: string;
}

export interface CreateDocumentoInput {
  nombre: string;
  mimeType: string;
  sizeBytes: number;
  ambito: "empresa" | "licitacion";
  subcarpeta: string;
  licitacionId?: string | null;
  tags?: string[];
  vigenciaHasta?: string | null;
}

export interface UpdateDocumentoInput {
  nombre?: string;
  subcarpeta?: string;
  tags?: string[];
  vigenciaHasta?: string | null;
}

function mockTree(): DocumentosTree {
  const licitaciones = new Map<string, DocumentosTree["licitaciones"][number]>();
  for (const doc of mock) {
    if (doc.ambito !== "licitacion" || !doc.licitacion) continue;
    const entry = licitaciones.get(doc.licitacion.id) ?? {
      ...doc.licitacion,
      total: 0,
      subcarpetas: DEFAULT_DOCUMENTOS_CONFIG.subcarpetas.licitacion.map((sub) => ({
        subcarpeta: sub,
        label: SUBCARPETA_LABELS[sub],
        total: 0,
      })),
    };
    entry.total += 1;
    const sub = entry.subcarpetas.find((s) => s.subcarpeta === doc.subcarpeta);
    if (sub) sub.total += 1;
    licitaciones.set(doc.licitacion.id, entry);
  }
  return {
    totalDocumentos: mock.length,
    totalBytes: mock.reduce((sum, d) => sum + (d.sizeBytes ?? 0), 0),
    empresa: {
      total: mock.filter((d) => d.ambito === "empresa").length,
      subcarpetas: DEFAULT_DOCUMENTOS_CONFIG.subcarpetas.empresa.map((sub) => ({
        subcarpeta: sub,
        label: SUBCARPETA_LABELS[sub],
        total: mock.filter((d) => d.ambito === "empresa" && d.subcarpeta === sub).length,
      })),
    },
    licitaciones: [...licitaciones.values()],
  };
}

function filterMock(params: DocumentosListParams): Documento[] {
  const q = params.q?.trim().toLowerCase();
  return mock.filter((doc) => {
    if (params.ambito && doc.ambito !== params.ambito) return false;
    if (params.licitacionId && doc.licitacionId !== params.licitacionId) return false;
    if (params.subcarpeta && doc.subcarpeta !== params.subcarpeta) return false;
    if (params.tag && !doc.tags.includes(params.tag)) return false;
    if (q && !doc.nombre.toLowerCase().includes(q)) return false;
    return true;
  });
}

function requireApi(): void {
  if (!hasApiBaseUrl) {
    throw new Error("Backend no configurado: define API_BASE_URL para gestionar documentos");
  }
}

export const documentosService = {
  config: () =>
    withFallback<DocumentosConfig>(
      async () => (await api.get<DocumentosConfig>("/documentos/config")).data,
      () => DEFAULT_DOCUMENTOS_CONFIG,
    ),

  list: (params: DocumentosListParams = {}) =>
    withFallback<Documento[]>(
      async () =>
        (
          await api.get<Documento[]>("/documentos", {
            params: Object.fromEntries(
              Object.entries(params).filter(([, value]) => value !== undefined && value !== ""),
            ),
          })
        ).data,
      () => filterMock(params),
    ),

  tree: () =>
    withFallback<DocumentosTree>(
      async () => (await api.get<DocumentosTree>("/documentos/arbol")).data,
      () => mockTree(),
    ),

  create: async (input: CreateDocumentoInput) => {
    requireApi();
    return (await api.post<DocumentoUploadTicket>("/documentos", input)).data;
  },

  /** Sube el binario directamente al bucket usando la URL firmada. */
  upload: async (url: string, file: File) => {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
    });
    if (!response.ok) {
      throw new Error(`No se pudo subir el archivo al bucket [${response.status}]`);
    }
  },

  confirm: async (id: string, sizeBytes: number) => {
    requireApi();
    return (await api.post<Documento>(`/documentos/${id}/confirmar`, { sizeBytes })).data;
  },

  downloadUrl: async (id: string) => {
    requireApi();
    return (await api.get<{ url: string }>(`/documentos/${id}/descarga`)).data;
  },

  update: async (id: string, input: UpdateDocumentoInput) => {
    requireApi();
    return (await api.patch<Documento>(`/documentos/${id}`, input)).data;
  },

  remove: async (id: string) => {
    requireApi();
    await api.delete(`/documentos/${id}`);
  },
};
